import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  BookOpen, Users, CheckCircle2, Clock, AlertTriangle, Award, FileCheck, TrendingDown,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import { getLdDashboard } from '../../services/trainingReportService';
import { formatDate } from '../../utils/Utils';

// Sayfa boşluğunu Layout verir; panolar kendi kenar padding'ini EKLEMEZ
// (referans: DashboardPage / PdksDashboardPage). Fazladan 24px kenar boşluğu
// bu sayfayı diğer panolardan dar gösteriyordu (müşteri geri bildirimi 2026-08-12).
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
`;

const KpiCard = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all 0.15s;
  &:hover { ${({ $clickable }) => $clickable && 'transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,66,66,0.12);'} }
`;

const KpiIconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg || '#E0F2FE'};
  color: ${({ $color }) => $color || '#0291EF'};
  flex-shrink: 0;
`;

const KpiLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-bottom: 2px;
`;

const KpiValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #004242;
  line-height: 1.1;
`;

const KpiSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const CardTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #004242;
  margin: 0 0 12px;
`;

const TopFailingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 13px;
  &:last-child { border-bottom: none; }
`;

const FailRateBadge = styled.span`
  color: ${({ $rate }) => $rate >= 50 ? '#ef4444' : $rate >= 30 ? '#f59e0b' : '#64748b'};
  font-weight: 700;
`;

export default function TrainingDashboardPage() {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getLdDashboard()
      .then((res) => setData(res.data?.data))
      .finally(() => setLoading(false));
  }, []);

  const trendData = useMemo(() => {
    if (!data?.last30DaysTrend) return [];
    return data.last30DaysTrend.map((d) => ({
      date: formatDate(d.date, i18n.language).slice(0, 5), // dd.MM
      completed: d.completed,
      assigned: d.assigned,
    }));
  }, [data, i18n.language]);

  const categoryData = useMemo(() => {
    if (!data?.byCategory) return [];
    return data.byCategory.slice(0, 8).map((c) => ({
      name: c.categoryName.length > 18 ? c.categoryName.substring(0, 16) + '…' : c.categoryName,
      completionRate: c.completionRate,
      total: c.total,
    }));
  }, [data]);

  if (loading) return <Container>{t('common.loading')}</Container>;
  if (!data) return <Container>{t('common.noData')}</Container>;

  return (
    <Container>

      <KpiGrid>
        <KpiCard>
          <KpiIconBox $bg="#E0F2FE" $color="#0291EF"><BookOpen size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.activeTrainings')}</KpiLabel>
            <KpiValue>{data.activeTrainings}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#F0FDF4" $color="#16A34A"><CheckCircle2 size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.completedThisMonth')}</KpiLabel>
            <KpiValue>{data.completedThisMonth}</KpiValue>
            <KpiSub>{t('trainingDashboard.ofTotal', { n: data.totalAssignments })}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#FEF3C7" $color="#F59E0B"><Clock size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.inProgress')}</KpiLabel>
            <KpiValue>{data.inProgressCount}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/training-reports?tab=overdue')}>
          <KpiIconBox $bg="#FEE2E2" $color="#DC2626"><AlertTriangle size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.overdue')}</KpiLabel>
            <KpiValue>{data.overdueCount}</KpiValue>
            <KpiSub>{t('trainingDashboard.clickForDetails')}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/training-reports?tab=compliance')}>
          <KpiIconBox $bg="#DBEAFE" $color="#2563EB"><FileCheck size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.complianceRate')}</KpiLabel>
            <KpiValue>{data.complianceRate != null ? `%${data.complianceRate}` : '—'}</KpiValue>
            <KpiSub>{t('trainingDashboard.mandatoryCoverage')}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard>
          <KpiIconBox $bg="#ECFDF5" $color="#10B981"><Award size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.avgCompletion')}</KpiLabel>
            <KpiValue>{data.averageCompletionRate != null ? `%${data.averageCompletionRate}` : '—'}</KpiValue>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/training-reports?tab=certifications')}>
          <KpiIconBox $bg="#FEF3C7" $color="#D97706"><AlertTriangle size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.expiringSoon')}</KpiLabel>
            <KpiValue>{data.expiringIn30Days}</KpiValue>
            <KpiSub>{t('trainingDashboard.expiredCount', { n: data.expiredCertificates })}</KpiSub>
          </div>
        </KpiCard>

        <KpiCard $clickable onClick={() => history.push('/training-pending-approvals')}>
          <KpiIconBox $bg="#F3E8FF" $color="#9333EA"><Users size={22} /></KpiIconBox>
          <div>
            <KpiLabel>{t('trainingDashboard.pendingApprovals')}</KpiLabel>
            <KpiValue>{data.pendingApprovals}</KpiValue>
          </div>
        </KpiCard>
      </KpiGrid>

      <ChartsGrid>
        <Card>
          <CardTitle>{t('trainingDashboard.last30DaysTrend')}</CardTitle>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="completed" name={t('trainingDashboard.completed')} stroke="#16A34A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="assigned" name={t('trainingDashboard.assigned')} stroke="#0291EF" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardTitle>{t('trainingDashboard.topFailing')}</CardTitle>
          {(data.topFailing || []).length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('trainingDashboard.noFailingData')}</div>
          ) : (
            data.topFailing.map((f) => (
              <TopFailingRow key={f.trainingId}>
                <div style={{ color: '#004242', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.title}
                </div>
                <div style={{ color: '#64748b', fontSize: 12 }}>
                  {f.failedAttempts}/{f.totalAttempts}
                </div>
                <FailRateBadge $rate={f.failRate}>%{f.failRate}</FailRateBadge>
              </TopFailingRow>
            ))
          )}
        </Card>
      </ChartsGrid>

      <Card>
        <CardTitle>{t('trainingDashboard.byCategory')}</CardTitle>
        {categoryData.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('common.noData')}</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
              <Tooltip formatter={(v, n) => n === 'completionRate' ? `%${v}` : v} />
              <Bar dataKey="completionRate" name={t('trainingDashboard.completionRate')} fill="#02D3BF" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </Container>
  );
}
